import * as fs from 'fs';
import * as fsp from 'fs/promises';
import { dirname } from 'path';

/**
 * Pairing config persisted at `${configDir}/config.json` (mode 0600).
 *
 * Holds:
 *  - the OAuth client credentials issued by Keep at pair time
 *  - the bound service identity id
 *  - the Keep API URL and discovered job pools
 *
 * Loaded on `<brand> run`; the substrate uses the credentials to mint a
 * fresh service token via Keep's `/api/service-tokens` endpoint.
 */
export interface PairingConfig {
  schemaVersion: 1;
  keepApiUrl: string;
  /** Jobs API base URL the paired Sifter claims from. Populated from the
   * BrandConfig keep-registration (overridable via `--jobs-url` on init). */
  jobsApiUrl: string;
  oauthClientId: string;
  serviceIdentity: {
    id: string;
    name: string;
    displayName: string;
  };
  credentials: {
    clientId: string;
    clientSecret: string;
  };
  pairing: {
    pairedAt: string;
    hostname: string;
    user: string;
  };
  /** Pools discovered from Keep's role-binding metadata after pairing. */
  jobPools: Array<{ id: string; name?: string }>;
}

export class PairingConfigStore {
  private readonly path: string;

  constructor(path: string) {
    this.path = path;
  }

  exists(): boolean {
    return fs.existsSync(this.path);
  }

  read(): PairingConfig {
    if (!fs.existsSync(this.path)) {
      throw new Error(
        `No pairing configuration found at ${this.path}. Run \`init\` first.`,
      );
    }
    const raw = fs.readFileSync(this.path, 'utf-8');
    const parsed = JSON.parse(raw) as PairingConfig;
    if (parsed.schemaVersion !== 1) {
      throw new Error(
        `Unsupported pairing config schema version: ${parsed.schemaVersion}`,
      );
    }
    return parsed;
  }

  async write(config: PairingConfig): Promise<void> {
    await fsp.mkdir(dirname(this.path), { recursive: true });
    const tmp = `${this.path}.tmp`;
    const fd = await fsp.open(tmp, 'w', 0o600);
    try {
      await fd.write(JSON.stringify(config, null, 2));
      await fd.sync();
    } finally {
      await fd.close();
    }
    await fsp.rename(tmp, this.path);
    // Ensure the final file has 0600 permissions even if rename preserved
    // a more permissive umask from the tmp creation.
    await fsp.chmod(this.path, 0o600);
  }
}
